import {
	infiniteQueryOptions,
	type MutationOptions,
	queryOptions,
	useMutation,
} from "@tanstack/react-query";
import { organizationService } from "./organization.service";
import type {
	CreateOrganizationUserInfo,
	CreateSubContractorInfo,
	CreateSubOrganizationInfo,
	DataAccessFilters,
	OrganizationParams,
	OrganizationUsersParams,
	ServiceResult,
	UpdateSubContractorInfo,
	UpdateSubOrganizationContactInfo,
} from "./organization.types";

// ==================== QUERY KEYS ====================

export const organizationKeys = {
	// Base keys
	all: ["organization"] as const,

	// Organizations
	organizations: () => [...organizationKeys.all, "organizations"] as const,
	organizationsList: (
		start: number,
		count: number,
		filters?: OrganizationParams,
	) =>
		[
			...organizationKeys.organizations(),
			"list",
			start,
			count,
			filters,
		] as const,
	organizationsDetail: (id: string) =>
		[...organizationKeys.organizations(), "detail", id] as const,
	organizationsCount: (params?: OrganizationParams) =>
		[...organizationKeys.organizations(), "count", params] as const,
	organizationsAll: (filters?: OrganizationParams) =>
		[...organizationKeys.organizations(), "all", filters] as const,
	organizationsStatuses: () =>
		[...organizationKeys.organizations(), "statuses"] as const,
	// Organization Roles (for specific organization)
	organizationRoles: (organizationId: string) =>
		[...organizationKeys.organizations(), organizationId, "roles"] as const,
	organizationRolesCount: (
		organizationId: string,
		params?: OrganizationParams,
	) =>
		[
			...organizationKeys.organizationRoles(organizationId),
			"count",
			params,
		] as const,
	organizationRolesList: (
		organizationId: string,
		start: number,
		count: number,
	) =>
		[
			...organizationKeys.organizationRoles(organizationId),
			"list",
			start,
			count,
		] as const,

	// Global Roles (both Platform and Organization scoped)
	globalRoles: () => [...organizationKeys.all, "global-roles"] as const,
	organizationRolePermissions: () =>
		[...organizationKeys.all, "organization-role-permissions"] as const,

	// Organization History
	organizationsHistory: (organizationId: string) =>
		[...organizationKeys.organizations(), "history", organizationId] as const,
	organizationsHistoryCount: (organizationId: string) =>
		[
			...organizationKeys.organizationsHistory(organizationId),
			"count",
		] as const,
	organizationsHistoryList: (
		organizationId: string,
		start: number,
		count: number,
	) =>
		[
			...organizationKeys.organizationsHistory(organizationId),
			"list",
			start,
			count,
		] as const,

	// Sub-Organization History
	subOrganizationsHistory: (orgId: string, subOrgId: string) =>
		[...organizationKeys.subOrganizations(orgId), "history", subOrgId] as const,
	subOrganizationsHistoryCount: (orgId: string, subOrgId: string) =>
		[
			...organizationKeys.subOrganizationsHistory(orgId, subOrgId),
			"count",
		] as const,
	subOrganizationsHistoryList: (
		orgId: string,
		subOrgId: string,
		start: number,
		count: number,
	) =>
		[
			...organizationKeys.subOrganizationsHistory(orgId, subOrgId),
			"list",
			start,
			count,
		] as const,

	// Sub-Contractor History
	subContractorsHistory: (orgId: string, subConId: string) =>
		[...organizationKeys.subContractors(orgId), "history", subConId] as const,
	subContractorsHistoryCount: (orgId: string, subConId: string) =>
		[
			...organizationKeys.subContractorsHistory(orgId, subConId),
			"count",
		] as const,
	subContractorsHistoryList: (
		orgId: string,
		subConId: string,
		start: number,
		count: number,
	) =>
		[
			...organizationKeys.subContractorsHistory(orgId, subConId),
			"list",
			start,
			count,
		] as const,

	// System-wide Organization Roles
	allOrganizationRoles: () => [...organizationKeys.all, "all-roles"] as const,
	allOrganizationRolesDetail: (roleId: string) =>
		[...organizationKeys.allOrganizationRoles(), "detail", roleId] as const,
	allOrganizationRolesList: (start: number, count: number) =>
		[...organizationKeys.allOrganizationRoles(), "list", start, count] as const,
	allOrganizationRolesCount: () =>
		[...organizationKeys.allOrganizationRoles(), "count"] as const,
	systemOrganizationRolesCount: (
		organizationId: string,
		params?: OrganizationParams,
	) =>
		[
			...organizationKeys.organizationRoles(organizationId),
			"count",
			params,
		] as const,

	// // Sub-Organizations
	// subOrganizations: (organizationId: string) =>
	// 	[
	// 		...organizationKeys.organizations(),
	// 		organizationId,
	// 		"sub-organizations",
	// 	] as const,

	// Sub-Organizations
	subOrganizations: (organizationId: string) =>
		[
			...organizationKeys.organizations(),
			organizationId,
			"sub-organizations",
		] as const,

	// Organization Contacts
	organizationContacts: (organizationId: string) =>
		[...organizationKeys.organizations(), organizationId, "contacts"] as const,

	subOrganizationsCount: (
		organizationId: string,
		params?: OrganizationParams,
	) =>
		[
			...organizationKeys.subOrganizations(organizationId),
			"count",
			params,
		] as const,
	subOrganizationsList: (
		organizationId: string,
		start: number,
		count: number,
		params?: OrganizationParams,
	) =>
		[
			...organizationKeys.subOrganizations(organizationId),
			"list",
			start,
			count,
			params,
		] as const,
	subOrganizationDetail: (organizationId: string, subOrganizationId: string) =>
		[
			...organizationKeys.subOrganizations(organizationId),
			"detail",
			subOrganizationId,
		] as const,

	// Sub-Contractors
	subContractors: (organizationId: string) =>
		[
			...organizationKeys.organizations(),
			organizationId,
			"sub-contractors",
		] as const,
	subContractorsCount: (organizationId: string, params?: OrganizationParams) =>
		[
			...organizationKeys.subContractors(organizationId),
			"count",
			params,
		] as const,
	subContractorsList: (
		organizationId: string,
		start: number,
		count: number,
		params?: OrganizationParams,
	) =>
		[
			...organizationKeys.subContractors(organizationId),
			"list",
			start,
			count,
			params,
		] as const,
	subContractorDetail: (organizationId: string, subContractorId: string) =>
		[
			...organizationKeys.subContractors(organizationId),
			"detail",
			subContractorId,
		] as const,

	// Email Check
	checkEmailUsage: (organizationId: string, email: string) =>
		[
			...organizationKeys.organizationUsers(organizationId),
			"check-email",
			email,
		] as const,

	// Organization OAuth
	organizationOAuth: (organizationId: string) =>
		[...organizationKeys.organizations(), organizationId, "oauth"] as const,

	// Organization Users
	organizationUsers: (organizationId: string) =>
		[...organizationKeys.organizations(), organizationId, "users"] as const,
	organizationUsersDetail: (
		organizationId: string,
		organizationUserId: string,
	) =>
		[
			...organizationKeys.organizationUsers(organizationId),
			"detail",
			organizationUserId, // Updated from userId per swagger2.json
		] as const,
	organizationUsersCount: (
		organizationId: string,
		params?: OrganizationUsersParams,
	) =>
		[
			...organizationKeys.organizationUsers(organizationId),
			"count",
			params,
		] as const,
	organizationUsersList: (
		organizationId: string,
		start: number,
		count: number,
		params?: OrganizationUsersParams,
	) =>
		[
			...organizationKeys.organizationUsers(organizationId),
			"list",
			start,
			count,
			params,
		] as const,
	organizationUsersAll: (
		organizationId: string,
		params?: OrganizationUsersParams,
	) =>
		[
			...organizationKeys.organizationUsers(organizationId),
			"all",
			params,
		] as const,
	organizationUsersEmailExists: (organizationId: string, email: string) =>
		[
			...organizationKeys.organizationUsers(organizationId),
			"email-exists",
			email,
		] as const,

	// Organization User Data Access
	organizationUserDataAccess: (
		organizationId: string,
		organizationUserId: string,
	) =>
		[
			...organizationKeys.organizationUsers(organizationId),
			organizationUserId,
			"data-access",
		] as const,
	organizationUserDataAccessList: (
		organizationId: string,
		organizationUserId: string,
		start: number,
		count: number,
		params?: DataAccessFilters,
	) =>
		[
			...organizationKeys.organizationUserDataAccess(
				organizationId,
				organizationUserId,
			),
			"list",
			start,
			count,
			params,
		] as const,
	organizationUserDataAccessCount: (
		organizationId: string,
		organizationUserId: string,
		params?: DataAccessFilters,
	) =>
		[
			...organizationKeys.organizationUserDataAccess(
				organizationId,
				organizationUserId,
			),
			"count",
			params,
		] as const,

	// Organization Data Access (org-level, NOT user-level)
	organizationDataAccess: (organizationId: string) =>
		[
			...organizationKeys.organizations(),
			organizationId,
			"org-data-access",
		] as const,
	organizationDataAccessCount: (
		organizationId: string,
		params?: DataAccessFilters,
	) =>
		[
			...organizationKeys.organizationDataAccess(organizationId),
			"count",
			params,
		] as const,
	organizationDataAccessList: (
		organizationId: string,
		start: number,
		count: number,
		params?: DataAccessFilters,
	) =>
		[
			...organizationKeys.organizationDataAccess(organizationId),
			"list",
			start,
			count,
			params,
		] as const,
} as const;

// ==================== ORGANIZATION QUERY OPTIONS ====================

export const organizationQueryOptions = {
	/**
	 * Get organizations with pagination and optional filtering
	 */
	list: (start: number = 0, count: number = 10, params?: OrganizationParams) =>
		queryOptions({
			queryKey: organizationKeys.organizationsList(start, count, params),
			queryFn: () => organizationService.getOrganizations(start, count, params),
			staleTime: 5 * 60 * 1000, // 5 minutes
			gcTime: 10 * 60 * 1000, // 10 minutes
		}),

	/**
	 * Get organizations count with optional filtering
	 */
	count: (params?: OrganizationParams) =>
		queryOptions({
			queryKey: organizationKeys.organizationsCount(params),
			queryFn: () => organizationService.getOrganizationsCount(params),
			staleTime: 5 * 60 * 1000, // 5 minutes
			gcTime: 10 * 60 * 1000, // 10 minutes
		}),

	/**
	 * Get a single organization by ID
	 */
	detail: (id: string) =>
		queryOptions({
			queryKey: organizationKeys.organizationsDetail(id),
			queryFn: () => organizationService.getOrganization(id),
			staleTime: 5 * 60 * 1000, // 5 minutes
			gcTime: 10 * 60 * 1000, // 10 minutes
			enabled: !!id && id.length > 0,
		}),

	/**
	 * Get all organizations (convenience method)
	 */
	all: (filters?: OrganizationParams) =>
		queryOptions({
			queryKey: organizationKeys.organizationsAll(filters),
			queryFn: () => organizationService.getAllOrganizations(filters),
			staleTime: 5 * 60 * 1000, // 5 minutes
			gcTime: 15 * 60 * 1000, // 15 minutes
			select: (data) => {
				// alphabetize the organizations by name
				return data.sort((a, b) => a.name?.localeCompare(b.name ?? "") ?? 0);
			},
		}),

	/**
	 * Infinite query for organizations
	 */
	infinite: (
		filters?: Omit<OrganizationParams, "page">,
		pageSize: number = 10,
	) =>
		infiniteQueryOptions({
			queryKey: [
				...organizationKeys.organizationsList(0, pageSize, filters),
				"infinite",
			],
			queryFn: ({ pageParam = 0 }) =>
				organizationService.getOrganizations(pageParam, pageSize, filters),
			initialPageParam: 0,
			getNextPageParam: (lastPage, _allPages, lastPageParam) => {
				// If the last page has fewer items than pageSize, we've reached the end
				if (lastPage.length < pageSize) {
					return undefined;
				}
				// Calculate next page start index
				return lastPageParam + pageSize;
			},
			getPreviousPageParam: (_firstPage, _allPages, firstPageParam) => {
				if (firstPageParam <= 0) {
					return undefined;
				}
				return Math.max(0, firstPageParam - pageSize);
			},
			staleTime: 5 * 60 * 1000, // 5 minutes
			gcTime: 10 * 60 * 1000, // 10 minutes
		}),

	/**
	 * Organization statuses
	 */
	statuses: () =>
		queryOptions({
			queryKey: organizationKeys.organizationsStatuses(),
			queryFn: () => organizationService.getOrganizationStatuses(),
			staleTime: 30 * 60 * 1000, // 30 minutes (statuses rarely change)
			gcTime: 60 * 60 * 1000, // 60 minutes
		}),

	// ==================== ORGANIZATION ROLES QUERY OPTIONS ====================

	permissions: {
		/**
		 * Assignable organization-role permissions.
		 * GET /organization-roles/permissions
		 */
		list: () =>
			queryOptions({
				queryKey: organizationKeys.organizationRolePermissions(),
				queryFn: () => organizationService.getOrganizationRolePermissions(),
				staleTime: 10 * 60 * 1000,
				gcTime: 30 * 60 * 1000,
			}),
	},

	roles: {
		/**
		 * Get all roles for an organization (unpaginated)
		 */
		list: (organizationId: string, start: number = 1, count: number = 10) =>
			queryOptions({
				queryKey: organizationKeys.organizationRoles(organizationId),
				queryFn: () =>
					organizationService.getOrganizationRoles(organizationId, {
						start,
						count,
					}),
				staleTime: 10 * 60 * 1000, // 10 minutes (roles change less frequently)
				gcTime: 30 * 60 * 1000, // 30 minutes
				enabled: !!organizationId && organizationId.length > 0,
				select: (data) => {
					// alphabetize the roles by name
					return data.sort((a, b) => a.name.localeCompare(b.name));
				},
			}),

		/**
		 * Get organization roles count with optional filtering
		 */
		count: (organizationId: string, params?: OrganizationParams) =>
			queryOptions({
				queryKey: organizationKeys.organizationRolesCount(
					organizationId,
					params,
				),
				queryFn: () =>
					organizationService.getOrganizationRolesCount(organizationId, params),
				staleTime: 10 * 60 * 1000, // 10 minutes
				gcTime: 30 * 60 * 1000, // 30 minutes
				enabled: !!organizationId && organizationId.length > 0,
			}),
	},

	// ==================== SYSTEM-WIDE ORGANIZATION ROLES QUERY OPTIONS ====================

	systemOrganizationRoles: {
		/**
		 * Get a single organization role by ID (system-wide)
		 * @param roleId - The role's UUID
		 */
		detail: (roleId: string) =>
			queryOptions({
				queryKey: organizationKeys.allOrganizationRolesDetail(roleId),
				queryFn: () => organizationService.getOrganizationRoleById(roleId),
				staleTime: 10 * 60 * 1000, // 10 minutes (roles change less frequently)
				gcTime: 30 * 60 * 1000, // 30 minutes
				enabled: !!roleId && roleId.length > 0,
			}),

		/**
		 * Get all organization roles (system-wide) count
		 */
		count: () =>
			queryOptions({
				queryKey: organizationKeys.allOrganizationRolesCount(),
				queryFn: () => organizationService.getAllOrganizationRolesCount(),
				staleTime: 10 * 60 * 1000, // 10 minutes (roles change less frequently)
				gcTime: 30 * 60 * 1000, // 30 minutes
			}),

		/**
		 * Get a paginated list of all organization roles (system-wide)
		 * @param start - Start index (1-based)
		 * @param count - Number of roles to fetch
		 */
		list: (start: number = 1, count: number = 10) =>
			queryOptions({
				queryKey: organizationKeys.allOrganizationRolesList(start, count),
				queryFn: () =>
					organizationService.getAllOrganizationRoles(start, count),
				staleTime: 10 * 60 * 1000, // 10 minutes (roles change less frequently)
				gcTime: 30 * 60 * 1000, // 30 minutes
			}),

		/**
		 * Infinite query for all organization roles (system-wide)
		 * @param pageSize - Number of roles per page
		 * @param totalCount - Optional total count for more accurate pagination
		 */
		infinite: (pageSize: number = 10, totalCount?: number) =>
			infiniteQueryOptions({
				queryKey: [
					...organizationKeys.allOrganizationRolesList(1, pageSize),
					"infinite",
				],
				queryFn: ({ pageParam }) =>
					organizationService.getAllOrganizationRoles(pageParam, pageSize),
				initialPageParam: 1, // API requires 1-based pagination
				getNextPageParam: (lastPage, allPages, lastPageParam) => {
					// If we have a total count, check against it for accuracy
					if (totalCount !== undefined) {
						const fetched = allPages.reduce(
							(acc, page) => acc + page.length,
							0,
						);
						if (fetched >= totalCount) {
							return undefined;
						}
					}

					// If the last page has fewer items than pageSize, we've reached the end
					if (lastPage.length < pageSize) {
						return undefined;
					}

					// Calculate next page start index (1-based pagination)
					return lastPageParam + pageSize;
				},
				getPreviousPageParam: (_firstPage, _allPages, firstPageParam) => {
					if (firstPageParam <= 1) {
						return undefined;
					}
					return Math.max(1, firstPageParam - pageSize);
				},
				staleTime: 10 * 60 * 1000, // 10 minutes (roles change less frequently)
				gcTime: 30 * 60 * 1000, // 30 minutes
			}),
	},

	// ==================== GLOBAL ROLES QUERY OPTIONS ====================

	globalRoles: {
		/**
		 * Get all global roles (both Platform and Organization scoped)
		 * GET /global-roles
		 */
		list: () =>
			queryOptions({
				queryKey: organizationKeys.globalRoles(),
				queryFn: () => organizationService.getGlobalRoles(),
				staleTime: 10 * 60 * 1000, // 10 minutes (roles change less frequently)
				gcTime: 30 * 60 * 1000, // 30 minutes
				select: (data) => {
					// Sort roles by scope (Platform first) then by name
					return data.sort((a, b) => {
						if (a.scope !== b.scope) {
							return a.scope === "Platform" ? -1 : 1;
						}
						return a.name.localeCompare(b.name);
					});
				},
			}),
	},

	// ==================== SYSTEM-WIDE ORGANIZATION ROLES QUERY OPTIONS ====================

	allRoles: {
		/**
		 * Get a single organization role by ID (system-wide)
		 * @param roleId - The role's UUID
		 */
		detail: (roleId: string) =>
			queryOptions({
				queryKey: organizationKeys.allOrganizationRolesDetail(roleId),
				queryFn: () => organizationService.getOrganizationRoleById(roleId),
				staleTime: 10 * 60 * 1000, // 10 minutes (roles change less frequently)
				gcTime: 30 * 60 * 1000, // 30 minutes
				enabled: !!roleId && roleId.length > 0,
			}),

		/**
		 * Get all organization roles (system-wide) count
		 */
		count: () =>
			queryOptions({
				queryKey: organizationKeys.allOrganizationRolesCount(),
				queryFn: () => organizationService.getAllOrganizationRolesCount(),
				staleTime: 10 * 60 * 1000, // 10 minutes (roles change less frequently)
				gcTime: 30 * 60 * 1000, // 30 minutes
			}),

		/**
		 * Get a paginated list of all organization roles (system-wide)
		 * @param start - Start index (1-based)
		 * @param count - Number of roles to fetch
		 */
		list: (start: number = 1, count: number = 10) =>
			queryOptions({
				queryKey: organizationKeys.allOrganizationRolesList(start, count),
				queryFn: () =>
					organizationService.getAllOrganizationRoles(start, count),
				staleTime: 10 * 60 * 1000, // 10 minutes (roles change less frequently)
				gcTime: 30 * 60 * 1000, // 30 minutes
			}),

		/**
		 * Infinite query for all organization roles (system-wide)
		 * @param pageSize - Number of roles per page
		 * @param totalCount - Optional total count for more accurate pagination
		 */
		infinite: (pageSize: number = 10, totalCount?: number) =>
			infiniteQueryOptions({
				queryKey: [
					...organizationKeys.allOrganizationRolesList(1, pageSize),
					"infinite",
				],
				queryFn: ({ pageParam }) =>
					organizationService.getAllOrganizationRoles(pageParam, pageSize),
				initialPageParam: 1, // API requires 1-based pagination
				getNextPageParam: (lastPage, allPages, lastPageParam) => {
					// If we have a total count, check against it for accuracy
					if (totalCount !== undefined) {
						const fetched = allPages.reduce(
							(acc, page) => acc + page.length,
							0,
						);
						if (fetched >= totalCount) {
							return undefined;
						}
					}

					// If the last page has fewer items than pageSize, we've reached the end
					if (lastPage.length < pageSize) {
						return undefined;
					}

					// Calculate next page start index (1-based pagination)
					return lastPageParam + pageSize;
				},
				getPreviousPageParam: (_firstPage, _allPages, firstPageParam) => {
					if (firstPageParam <= 1) {
						return undefined;
					}
					return Math.max(1, firstPageParam - pageSize);
				},
				staleTime: 10 * 60 * 1000, // 10 minutes (roles change less frequently)
				gcTime: 30 * 60 * 1000, // 30 minutes
			}),
	},

	// ==================== ORGANIZATION CONTACTS QUERY OPTIONS ====================

	contacts: {
		/**
		 * Get all contacts for an organization
		 */
		list: (organizationId: string) =>
			queryOptions({
				queryKey: organizationKeys.organizationContacts(organizationId),
				queryFn: () =>
					organizationService.getOrganizationContacts(organizationId),
				staleTime: 5 * 60 * 1000, // 5 minutes
				gcTime: 15 * 60 * 1000, // 15 minutes
				enabled: !!organizationId && organizationId.length > 0,
			}),
	},

	// ==================== ORGANIZATION OAUTH QUERY OPTIONS ====================

	oauth: {
		/**
		 * Get OAuth provider information for an organization
		 */
		detail: (organizationId: string) =>
			queryOptions({
				queryKey: organizationKeys.organizationOAuth(organizationId),
				queryFn: () =>
					organizationService.getOrganizationOAuthInfo(organizationId),
				staleTime: 15 * 60 * 1000, // 15 minutes (OAuth settings change rarely)
				gcTime: 60 * 60 * 1000, // 1 hour
				enabled: !!organizationId && organizationId.length > 0,
			}),
	},

	// ==================== ORGANIZATION USERS QUERY OPTIONS ====================

	users: {
		/**
		 * Get organization users with pagination and optional filtering
		 */
		list: (
			organizationId: string,
			start: number = 1,
			count: number = 10,
			filters?: OrganizationUsersParams,
		) =>
			queryOptions({
				queryKey: organizationKeys.organizationUsersList(
					organizationId,
					start,
					count,
					filters,
				),
				queryFn: () =>
					organizationService.getOrganizationUsers(
						organizationId,
						start,
						count,
						filters,
					),
				staleTime: 5 * 60 * 1000, // 5 minutes (user data changes more frequently)
				gcTime: 5 * 60 * 1000, // 5 minutes
				enabled: !!organizationId && organizationId.length > 0,
			}),

		/**
		 * Get organization users count with optional filtering
		 */
		count: (organizationId: string, filters?: OrganizationUsersParams) =>
			queryOptions({
				queryKey: organizationKeys.organizationUsersCount(
					organizationId,
					filters,
				),
				queryFn: () =>
					organizationService.getOrganizationUsersCount(
						organizationId,
						filters,
					),
				staleTime: 5 * 60 * 1000, // 5 minutes
				gcTime: 5 * 60 * 1000, // 5 minutes
				enabled: !!organizationId && organizationId.length > 0,
			}),

		/**
		 * Get a specific organization user
		 * @param organizationId - The organization ID
		 * @param organizationUserId - The organization user ID (join table ID, not platform user ID)
		 */
		detail: (organizationId: string, organizationUserId: string) =>
			queryOptions({
				queryKey: organizationKeys.organizationUsersDetail(
					organizationId,
					organizationUserId,
				),
				queryFn: () =>
					organizationService.getOrganizationUser(
						organizationId,
						organizationUserId,
					),
				staleTime: 5 * 60 * 1000, // 5 minutes
				gcTime: 5 * 60 * 1000, // 5 minutes
				enabled:
					!!organizationId &&
					organizationId.length > 0 &&
					!!organizationUserId &&
					organizationUserId.length > 0,
			}),

		/**
		 * Get all organization users (convenience method)
		 */
		all: (organizationId: string, filters?: OrganizationUsersParams) =>
			queryOptions({
				queryKey: organizationKeys.organizationUsersAll(
					organizationId,
					filters,
				),
				queryFn: () =>
					organizationService.getAllOrganizationUsers(organizationId, filters),
				staleTime: 5 * 60 * 1000, // 5 minutes
				gcTime: 5 * 60 * 1000, // 5 minutes
				enabled: !!organizationId && organizationId.length > 0,
			}),

		/**
		 * Check if an email is already in use within an organization
		 * Returns true if the email exists, false otherwise
		 * Useful for validating email uniqueness during user creation/update
		 */
		emailExists: (organizationId: string, email: string) =>
			queryOptions({
				queryKey: organizationKeys.organizationUsersEmailExists(
					organizationId,
					email,
				),
				queryFn: async () => {
					const count = await organizationService.getOrganizationUsersCount(
						organizationId,
						{ Email: email },
					);
					return count > 0;
				},
				staleTime: 30 * 1000, // 30 seconds (email availability can change)
				gcTime: 60 * 1000, // 1 minute
				enabled:
					!!organizationId &&
					organizationId.length > 0 &&
					!!email &&
					email.length > 0 &&
					email.includes("@"),
			}),

		/**
		 * Infinite query for organization users
		 * @param totalCount - Optional total count for more accurate pagination
		 */
		infinite: (
			organizationId: string,
			filters?: OrganizationUsersParams,
			pageSize: number = 10,
			totalCount?: number,
		) =>
			infiniteQueryOptions({
				queryKey: [
					...organizationKeys.organizationUsersList(
						organizationId,
						1, // API uses 1-based pagination
						pageSize,
						filters,
					),
					"infinite",
				],
				queryFn: ({ pageParam }) =>
					organizationService.getOrganizationUsers(
						organizationId,
						pageParam, // No default needed - initialPageParam handles it
						pageSize,
						filters,
					),
				initialPageParam: 1, // API requires 1-based pagination
				getNextPageParam: (lastPage, allPages, lastPageParam) => {
					// If we have a total count, check against it for accuracy
					if (totalCount !== undefined) {
						const fetched = allPages.reduce(
							(acc, page) => acc + page.length,
							0,
						);
						if (fetched >= totalCount) {
							return undefined;
						}
					}

					// If the last page has fewer items than pageSize, we've reached the end
					if (lastPage.length < pageSize) {
						return undefined;
					}

					// Calculate next page start index (1-based pagination)
					return lastPageParam + pageSize;
				},
				getPreviousPageParam: (_firstPage, _allPages, firstPageParam) => {
					if (firstPageParam <= 1) {
						return undefined;
					}
					return Math.max(1, firstPageParam - pageSize);
				},
				staleTime: 5 * 60 * 1000, // 5 minutes
				gcTime: 5 * 60 * 1000, // 5 minutes
				enabled: !!organizationId && organizationId.length > 0,
			}),
	},

	// ==================== ORGANIZATION USER DATA ACCESS QUERY OPTIONS ====================

	dataAccess: {
		/**
		 * Get data access entries for an organization user with pagination
		 * @param organizationId - The organization ID
		 * @param organizationUserId - The organization user ID (join table ID)
		 * @param start - Pagination start index
		 * @param count - Number of items to return
		 * @param filters - Optional filters
		 */
		list: (
			organizationId: string,
			organizationUserId: string,
			start: number = 1,
			count: number = 10,
			filters?: DataAccessFilters,
		) =>
			queryOptions({
				queryKey: organizationKeys.organizationUserDataAccessList(
					organizationId,
					organizationUserId,
					start,
					count,
					filters,
				),
				queryFn: () =>
					organizationService.getOrganizationUserDataAccess(
						organizationId,
						organizationUserId,
						start,
						count,
						filters,
					),
				staleTime: 5 * 60 * 1000, // 5 minutes
				gcTime: 5 * 60 * 1000, // 5 minutes
				enabled:
					!!organizationId &&
					organizationId.length > 0 &&
					!!organizationUserId &&
					organizationUserId.length > 0,
			}),

		/**
		 * Get count of data access entries for an organization user
		 * @param organizationId - The organization ID
		 * @param organizationUserId - The organization user ID (join table ID)
		 * @param filters - Optional filters
		 */
		count: (
			organizationId: string,
			organizationUserId: string,
			filters?: DataAccessFilters,
		) =>
			queryOptions({
				queryKey: organizationKeys.organizationUserDataAccessCount(
					organizationId,
					organizationUserId,
					filters,
				),
				queryFn: () =>
					organizationService.getOrganizationUserDataAccessCount(
						organizationId,
						organizationUserId,
						filters,
					),
				staleTime: 5 * 60 * 1000, // 5 minutes
				gcTime: 5 * 60 * 1000, // 5 minutes
				enabled:
					!!organizationId &&
					organizationId.length > 0 &&
					!!organizationUserId &&
					organizationUserId.length > 0,
			}),
	},

	// ==================== ORGANIZATION DATA ACCESS QUERY OPTIONS ====================

	organizationDataAccess: {
		/**
		 * Get data access count for an organization
		 * GET /organizations/{organizationId}/data-access/count
		 */
		count: (organizationId: string, filters?: DataAccessFilters) =>
			queryOptions({
				queryKey: organizationKeys.organizationDataAccessCount(
					organizationId,
					filters,
				),
				queryFn: () =>
					organizationService.getOrganizationDataAccessCount(
						organizationId,
						filters,
					),
				staleTime: 5 * 60 * 1000, // 5 minutes
				gcTime: 10 * 60 * 1000, // 10 minutes
				enabled: !!organizationId && organizationId.length > 0,
			}),

		/**
		 * Get paginated data access list for an organization
		 * GET /organizations/{organizationId}/data-access/{start}/{count}
		 */
		list: (
			organizationId: string,
			start: number = 1,
			count: number = 10,
			filters?: DataAccessFilters,
		) =>
			queryOptions({
				queryKey: organizationKeys.organizationDataAccessList(
					organizationId,
					start,
					count,
					filters,
				),
				queryFn: () =>
					organizationService.getOrganizationDataAccess(
						organizationId,
						start,
						count,
						filters,
					),
				staleTime: 5 * 60 * 1000, // 5 minutes
				gcTime: 10 * 60 * 1000, // 10 minutes
				enabled: !!organizationId && organizationId.length > 0,
			}),
	},

	// ==================== SUB-ORGANIZATIONS QUERY OPTIONS ====================

	subOrganizations: {
		/**
		 * Get sub-organizations with pagination and optional filtering
		 */
		list: (
			organizationId: string,
			start: number = 1,
			count: number = 10,
			params?: OrganizationParams,
		) =>
			queryOptions({
				queryKey: organizationKeys.subOrganizationsList(
					organizationId,
					start,
					count,
					params,
				),
				queryFn: () =>
					organizationService.getSubOrganizations(
						organizationId,
						start,
						count,
						params,
					),
				staleTime: 5 * 60 * 1000, // 5 minutes
				gcTime: 10 * 60 * 1000, // 10 minutes
				enabled: !!organizationId && organizationId.length > 0,
				select: (data) => {
					return data.sort((a, b) =>
						(a.name ?? a.id ?? "").localeCompare(
							b.name ?? b.id ?? "",
							undefined,
							{ sensitivity: "base" },
						),
					);
				},
			}),

		/**
		 * Get sub-organizations count with optional filtering
		 */
		count: (organizationId: string, params?: OrganizationParams) =>
			queryOptions({
				queryKey: organizationKeys.subOrganizationsCount(
					organizationId,
					params,
				),
				queryFn: () =>
					organizationService.getSubOrganizationsCount(organizationId, params),
				staleTime: 5 * 60 * 1000, // 5 minutes
				gcTime: 10 * 60 * 1000, // 10 minutes
				enabled: !!organizationId && organizationId.length > 0,
			}),

		/**
		 * Get a single sub-organization by ID
		 * GET /organizations/{organizationId}/sub-organizations/{subOrganizationId}
		 */
		detail: (organizationId: string, subOrganizationId: string) =>
			queryOptions({
				queryKey: organizationKeys.subOrganizationDetail(
					organizationId,
					subOrganizationId,
				),
				queryFn: () =>
					organizationService.getSubOrganization(
						organizationId,
						subOrganizationId,
					),
				staleTime: 5 * 60 * 1000, // 5 minutes
				gcTime: 10 * 60 * 1000, // 10 minutes
				enabled:
					!!organizationId &&
					organizationId.length > 0 &&
					!!subOrganizationId &&
					subOrganizationId.length > 0,
			}),

		/**
		 * Infinite query for sub-organizations
		 * @param totalCount - Optional total count for more accurate pagination
		 */
		infinite: (
			organizationId: string,
			filters?: OrganizationParams,
			pageSize: number = 10,
			totalCount?: number,
		) =>
			infiniteQueryOptions({
				queryKey: [
					...organizationKeys.subOrganizationsList(
						organizationId,
						1, // API uses 1-based pagination
						pageSize,
						filters,
					),
					"infinite",
				],
				queryFn: ({ pageParam }) =>
					organizationService.getSubOrganizations(
						organizationId,
						pageParam,
						pageSize,
						filters,
					),
				initialPageParam: 1, // API requires 1-based pagination
				getNextPageParam: (lastPage, allPages, lastPageParam) => {
					// If we have a total count, check against it for accuracy
					if (totalCount !== undefined) {
						const fetched = allPages.reduce(
							(acc, page) => acc + page.length,
							0,
						);
						if (fetched >= totalCount) {
							return undefined;
						}
					}

					// If the last page has fewer items than pageSize, we've reached the end
					if (lastPage.length < pageSize) {
						return undefined;
					}

					// Calculate next page start index (1-based pagination)
					return lastPageParam + pageSize;
				},
				getPreviousPageParam: (_firstPage, _allPages, firstPageParam) => {
					if (firstPageParam <= 1) {
						return undefined;
					}
					return Math.max(1, firstPageParam - pageSize);
				},
				staleTime: 5 * 60 * 1000, // 5 minutes
				gcTime: 5 * 60 * 1000, // 5 minutes
				enabled: !!organizationId && organizationId.length > 0,
			}),
	},

	// ==================== SUB-CONTRACTORS QUERY OPTIONS ====================

	subContractors: {
		/**
		 * Get sub-contractors with pagination and optional filtering
		 */
		list: (
			organizationId: string,
			start: number = 1,
			count: number = 10,
			params?: OrganizationParams,
		) =>
			queryOptions({
				queryKey: organizationKeys.subContractorsList(
					organizationId,
					start,
					count,
					params,
				),
				queryFn: () =>
					organizationService.getSubContractors(
						organizationId,
						start,
						count,
						params,
					),
				staleTime: 5 * 60 * 1000, // 5 minutes
				gcTime: 10 * 60 * 1000, // 10 minutes
				enabled: !!organizationId && organizationId.length > 0,
			}),

		/**
		 * Get sub-contractors count with optional filtering
		 */
		count: (organizationId: string, params?: OrganizationParams) =>
			queryOptions({
				queryKey: organizationKeys.subContractorsCount(organizationId, params),
				queryFn: () =>
					organizationService.getSubContractorsCount(organizationId, params),
				staleTime: 5 * 60 * 1000, // 5 minutes
				gcTime: 10 * 60 * 1000, // 10 minutes
				enabled: !!organizationId && organizationId.length > 0,
			}),

		/**
		 * Get a single sub-contractor by ID
		 * GET /organizations/{organizationId}/sub-contractors/{subContractorId}
		 */
		detail: (organizationId: string, subContractorId: string) =>
			queryOptions({
				queryKey: organizationKeys.subContractorDetail(
					organizationId,
					subContractorId,
				),
				queryFn: () =>
					organizationService.getSubContractor(organizationId, subContractorId),
				staleTime: 5 * 60 * 1000, // 5 minutes
				gcTime: 10 * 60 * 1000, // 10 minutes
				enabled:
					!!organizationId &&
					organizationId.length > 0 &&
					!!subContractorId &&
					subContractorId.length > 0,
			}),

		/**
		 * Infinite query for sub-contractors
		 * @param totalCount - Optional total count for more accurate pagination
		 */
		infinite: (
			organizationId: string,
			filters?: OrganizationParams,
			pageSize: number = 10,
			totalCount?: number,
		) =>
			infiniteQueryOptions({
				queryKey: [
					...organizationKeys.subContractorsList(
						organizationId,
						1, // API uses 1-based pagination
						pageSize,
						filters,
					),
					"infinite",
				],
				queryFn: ({ pageParam }) =>
					organizationService.getSubContractors(
						organizationId,
						pageParam,
						pageSize,
						filters,
					),
				initialPageParam: 1, // API requires 1-based pagination
				getNextPageParam: (lastPage, allPages, lastPageParam) => {
					// If we have a total count, check against it for accuracy
					if (totalCount !== undefined) {
						const fetched = allPages.reduce(
							(acc, page) => acc + page.length,
							0,
						);
						if (fetched >= totalCount) {
							return undefined;
						}
					}

					// If the last page has fewer items than pageSize, we've reached the end
					if (lastPage.length < pageSize) {
						return undefined;
					}

					// Calculate next page start index (1-based pagination)
					return lastPageParam + pageSize;
				},
				getPreviousPageParam: (_firstPage, _allPages, firstPageParam) => {
					if (firstPageParam <= 1) {
						return undefined;
					}
					return Math.max(1, firstPageParam - pageSize);
				},
				staleTime: 5 * 60 * 1000, // 5 minutes
				gcTime: 5 * 60 * 1000, // 5 minutes
				enabled: !!organizationId && organizationId.length > 0,
			}),
	},

	// ==================== EMAIL CHECK QUERY OPTIONS ====================

	emailCheck: {
		/**
		 * Check if an email is in use within the organization or globally
		 * GET /organizations/{organizationId}/users/check-email
		 */
		checkUsage: (organizationId: string, email: string) =>
			queryOptions({
				queryKey: organizationKeys.checkEmailUsage(organizationId, email),
				queryFn: () =>
					organizationService.checkEmailUsage(organizationId, email),
				staleTime: 30 * 1000, // 30 seconds (email availability can change)
				gcTime: 60 * 1000, // 1 minute
				enabled:
					!!organizationId &&
					organizationId.length > 0 &&
					!!email &&
					email.length > 0 &&
					email.includes("@"),
			}),
	},

	// ==================== HEALTH CHECK QUERY OPTIONS ====================

	health: {
		/**
		 * Check API heartbeat
		 * GET /heartbeat
		 */
		heartbeat: () =>
			queryOptions({
				queryKey: [...organizationKeys.all, "heartbeat"] as const,
				queryFn: () => organizationService.checkHeartbeat(),
				staleTime: 30 * 1000, // 30 seconds (health checks should be fresh)
				gcTime: 60 * 1000, // 1 minute
			}),
	},

	// ==================== ORGANIZATION HISTORY QUERY OPTIONS ====================

	history: {
		count: (organizationId: string) =>
			queryOptions({
				queryKey: organizationKeys.organizationsHistoryCount(organizationId),
				queryFn: () =>
					organizationService.getOrganizationHistoryCount(organizationId),
				enabled: !!organizationId,
			}),

		infinite: (
			organizationId: string,
			pageSize: number,
			totalCount: number | undefined,
		) =>
			infiniteQueryOptions({
				queryKey: [
					...organizationKeys.organizationsHistoryList(
						organizationId,
						1,
						pageSize,
					),
					"infinite",
					totalCount,
				],
				queryFn: ({ pageParam }) =>
					organizationService.getOrganizationHistory(
						organizationId,
						pageParam,
						pageSize,
					),
				initialPageParam: Math.max(1, (totalCount ?? 0) - pageSize + 1),
				getNextPageParam: (_lastPage, _allPages, lastPageParam) => {
					if (lastPageParam <= 1) return undefined;
					return Math.max(1, lastPageParam - pageSize);
				},
				getPreviousPageParam: (_firstPage, _allPages, firstPageParam) => {
					if (!totalCount || firstPageParam >= totalCount - pageSize + 1)
						return undefined;
					return firstPageParam + pageSize;
				},
				enabled: !!organizationId && totalCount !== undefined && totalCount > 0,
			}),
	},

	// ==================== SUB-ORGANIZATION HISTORY QUERY OPTIONS ====================

	subOrganizationHistory: {
		count: (orgId: string, subOrgId: string) =>
			queryOptions({
				queryKey: organizationKeys.subOrganizationsHistoryCount(
					orgId,
					subOrgId,
				),
				queryFn: () =>
					organizationService.getSubOrganizationHistoryCount(orgId, subOrgId),
				enabled: !!orgId && !!subOrgId,
			}),

		infinite: (
			orgId: string,
			subOrgId: string,
			pageSize: number,
			totalCount: number | undefined,
		) =>
			infiniteQueryOptions({
				queryKey: [
					...organizationKeys.subOrganizationsHistoryList(
						orgId,
						subOrgId,
						1,
						pageSize,
					),
					"infinite",
					totalCount,
				],
				queryFn: ({ pageParam }) =>
					organizationService.getSubOrganizationHistory(
						orgId,
						subOrgId,
						pageParam,
						pageSize,
					),
				initialPageParam: Math.max(1, (totalCount ?? 0) - pageSize + 1),
				getNextPageParam: (_lastPage, _allPages, lastPageParam) => {
					if (lastPageParam <= 1) return undefined;
					return Math.max(1, lastPageParam - pageSize);
				},
				getPreviousPageParam: (_firstPage, _allPages, firstPageParam) => {
					if (!totalCount || firstPageParam >= totalCount - pageSize + 1)
						return undefined;
					return firstPageParam + pageSize;
				},
				enabled:
					!!orgId && !!subOrgId && totalCount !== undefined && totalCount > 0,
			}),
	},

	// ==================== SUB-CONTRACTOR HISTORY QUERY OPTIONS ====================

	subContractorHistory: {
		count: (orgId: string, subConId: string) =>
			queryOptions({
				queryKey: organizationKeys.subContractorsHistoryCount(orgId, subConId),
				queryFn: () =>
					organizationService.getSubContractorHistoryCount(orgId, subConId),
				enabled: !!orgId && !!subConId,
			}),

		infinite: (
			orgId: string,
			subConId: string,
			pageSize: number,
			totalCount: number | undefined,
		) =>
			infiniteQueryOptions({
				queryKey: [
					...organizationKeys.subContractorsHistoryList(
						orgId,
						subConId,
						1,
						pageSize,
					),
					"infinite",
					totalCount,
				],
				queryFn: ({ pageParam }) =>
					organizationService.getSubContractorHistory(
						orgId,
						subConId,
						pageParam,
						pageSize,
					),
				initialPageParam: Math.max(1, (totalCount ?? 0) - pageSize + 1),
				getNextPageParam: (_lastPage, _allPages, lastPageParam) => {
					if (lastPageParam <= 1) return undefined;
					return Math.max(1, lastPageParam - pageSize);
				},
				getPreviousPageParam: (_firstPage, _allPages, firstPageParam) => {
					if (!totalCount || firstPageParam >= totalCount - pageSize + 1)
						return undefined;
					return firstPageParam + pageSize;
				},
				enabled:
					!!orgId && !!subConId && totalCount !== undefined && totalCount > 0,
			}),
	},
};

// ==================== CONVENIENCE EXPORTS ====================

/**
 * All organization query options grouped by category
 */
export const organizationQueries = {
	// Core organization queries
	organizations: organizationQueryOptions,

	// Nested resource queries
	roles: organizationQueryOptions.roles,
	globalRoles: organizationQueryOptions.globalRoles,
	allRoles: organizationQueryOptions.allRoles,
	contacts: organizationQueryOptions.contacts,
	oauth: organizationQueryOptions.oauth,
	users: organizationQueryOptions.users,
	subOrganizations: organizationQueryOptions.subOrganizations,
	subContractors: organizationQueryOptions.subContractors,
	dataAccess: organizationQueryOptions.dataAccess,
	organizationDataAccess: organizationQueryOptions.organizationDataAccess,
	emailCheck: organizationQueryOptions.emailCheck,
} as const;

/**
 * Query key factory for easy invalidation
 */
export const organizationQueryKeys = organizationKeys;

// ==================== UTILITY FUNCTIONS ====================

/**
 * Helper to invalidate all organization-related queries
 */
export function getOrganizationInvalidationKeys() {
	return {
		all: organizationKeys.all,
		organizations: organizationKeys.organizations(),
		organizationDetail: (id: string) =>
			organizationKeys.organizationsDetail(id),
		organizationRoles: (organizationId: string) =>
			organizationKeys.organizationRoles(organizationId),
		organizationContacts: (organizationId: string) =>
			organizationKeys.organizationContacts(organizationId),
		organizationOAuth: (organizationId: string) =>
			organizationKeys.organizationOAuth(organizationId),
		organizationUsers: (organizationId: string) =>
			organizationKeys.organizationUsers(organizationId),
		organizationUsersEmailExists: (organizationId: string, email: string) =>
			organizationKeys.organizationUsersEmailExists(organizationId, email),
		organizationUserDataAccess: (
			organizationId: string,
			organizationUserId: string,
		) =>
			organizationKeys.organizationUserDataAccess(
				organizationId,
				organizationUserId,
			),
		organizationDataAccess: (organizationId: string) =>
			organizationKeys.organizationDataAccess(organizationId),
		subOrganizations: (organizationId: string) =>
			organizationKeys.subOrganizations(organizationId),
		subOrganizationDetail: (
			organizationId: string,
			subOrganizationId: string,
		) =>
			organizationKeys.subOrganizationDetail(organizationId, subOrganizationId),
		subContractors: (organizationId: string) =>
			organizationKeys.subContractors(organizationId),
		subContractorDetail: (organizationId: string, subContractorId: string) =>
			organizationKeys.subContractorDetail(organizationId, subContractorId),
		checkEmailUsage: (organizationId: string, email: string) =>
			organizationKeys.checkEmailUsage(organizationId, email),
	};
}

/**
 * Helper to get query keys for a specific organization and all its nested resources
 */
export function getOrganizationRelatedKeys(organizationId: string) {
	return {
		organization: organizationKeys.organizationsDetail(organizationId),
		roles: organizationKeys.organizationRoles(organizationId),
		contacts: organizationKeys.organizationContacts(organizationId),
		oauth: organizationKeys.organizationOAuth(organizationId),
		users: organizationKeys.organizationUsers(organizationId),
		subOrganizations: organizationKeys.subOrganizations(organizationId),
		subContractors: organizationKeys.subContractors(organizationId),
		dataAccess: organizationKeys.organizationDataAccess(organizationId),
	};
}

/**
 * Helper to create query options with consistent defaults
 */
export function createOrganizationQueryOptions<T>(
	queryKey: readonly unknown[],
	queryFn: () => Promise<T>,
	options: {
		staleTime?: number;
		gcTime?: number;
		enabled?: boolean;
	} = {},
) {
	return queryOptions({
		queryKey,
		queryFn,
		staleTime: options.staleTime ?? 5 * 60 * 1000, // Default 5 minutes
		gcTime: options.gcTime ?? 10 * 60 * 1000, // Default 10 minutes
		enabled: options.enabled ?? true,
	});
}

// ==================== MUTATION OPTIONS ====================

/**
 * Mutation options for organization-related operations
 */
export const organizationMutationOptions = {
	// ==================== SUB-ORGANIZATION MUTATIONS ====================

	subOrganizations: {
		/**
		 * Create a new sub-organization
		 * POST /organizations/{organizationId}/sub-organizations
		 */
		create: (
			organizationId: string,
		): MutationOptions<ServiceResult, Error, CreateSubOrganizationInfo> => ({
			mutationFn: (data: CreateSubOrganizationInfo) =>
				organizationService.createSubOrganization(organizationId, data),
			// Note: Cache invalidation should be handled in the component using onSuccess
		}),

		/**
		 * Update an existing sub-organization
		 * PUT /organizations/{organizationId}/sub-organizations/{subOrganizationId}
		 */
		update: (
			organizationId: string,
			subOrganizationId: string,
		): MutationOptions<void, Error, UpdateSubOrganizationContactInfo> => ({
			mutationFn: (data: UpdateSubOrganizationContactInfo) =>
				organizationService.updateSubOrganization(
					organizationId,
					subOrganizationId,
					data,
				),
			// Note: Cache invalidation should be handled in the component using onSuccess
		}),
	},

	// ==================== SUB-CONTRACTOR MUTATIONS ====================

	subContractors: {
		/**
		 * Create a new sub-contractor
		 * POST /organizations/{organizationId}/sub-contractors
		 */
		create: (
			organizationId: string,
		): MutationOptions<ServiceResult, Error, CreateSubContractorInfo> => ({
			mutationFn: (data: CreateSubContractorInfo) =>
				organizationService.createSubContractor(organizationId, data),
			// Note: Cache invalidation should be handled in the component using onSuccess
		}),

		/**
		 * Update an existing sub-contractor
		 * PUT /organizations/{organizationId}/sub-contractors/{subContractorId}
		 */
		update: (
			organizationId: string,
			subContractorId: string,
		): MutationOptions<void, Error, UpdateSubContractorInfo> => ({
			mutationFn: (data: UpdateSubContractorInfo) =>
				organizationService.updateSubContractor(
					organizationId,
					subContractorId,
					data,
				),
			// Note: Cache invalidation should be handled in the component using onSuccess
		}),
	},

	// ==================== USER MUTATIONS ====================

	users: {
		/**
		 * Bulk create organization users
		 * POST /organizations/{organizationId}/users/bulk
		 */
		bulkCreate: (
			organizationId: string,
		): MutationOptions<ServiceResult, Error, CreateOrganizationUserInfo[]> => ({
			mutationFn: (users: CreateOrganizationUserInfo[]) =>
				organizationService.bulkCreateOrganizationUsers(organizationId, users),
			// Note: Cache invalidation should be handled in the component using onSuccess
		}),
	},
};

/**
 * Convenience export for mutations
 */
export const organizationMutations = organizationMutationOptions;

// ==================== EXPORT HOOKS ====================

/**
 * Hook to export organization users to CSV
 * @param organizationId - The organization ID
 */
export function useExportOrganizationUsers(organizationId: string) {
	return useMutation({
		mutationFn: ({
			startIndex,
			count,
			params,
		}: {
			startIndex: number;
			count: number;
			params?: OrganizationUsersParams & { columns?: string };
		}) =>
			organizationService.exportOrganizationUsers(
				organizationId,
				startIndex,
				count,
				params,
			),
	});
}

/**
 * Hook to export sub-organizations to CSV
 * @param organizationId - The organization ID
 */
export function useExportSubOrganizations(organizationId: string) {
	return useMutation({
		mutationFn: ({
			startIndex,
			count,
			params,
		}: {
			startIndex: number;
			count: number;
			params?: OrganizationParams & { columns?: string };
		}) =>
			organizationService.exportSubOrganizations(
				organizationId,
				startIndex,
				count,
				params,
			),
	});
}

/**
 * Hook to export sub-contractors to CSV
 * @param organizationId - The organization ID
 */
export function useExportSubContractors(organizationId: string) {
	return useMutation({
		mutationFn: ({
			startIndex,
			count,
			params,
		}: {
			startIndex: number;
			count: number;
			params?: OrganizationParams & { columns?: string };
		}) =>
			organizationService.exportSubContractors(
				organizationId,
				startIndex,
				count,
				params,
			),
	});
}
